import { useState, useCallback } from 'react';
import api from '../api/axios';

export interface DisponibilidadStaffRow {
  id: number;
  idUsuario: number;
  diaSemana: number; // 0 = lunes ... 6 = domingo
  horaInicio: string; // HH:mm:ss
  horaFin: string; // HH:mm:ss
  comentario?: string | null;
  usuario?: {
    id: number;
    nombre?: string;
    email?: string;
  };
}

export interface CreateDisponibilidadStaffDto {
  idUsuario: number;
  diaSemana: number;
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm
  comentario?: string;
}

export type UpdateDisponibilidadStaffDto = Partial<CreateDisponibilidadStaffDto>;

export function useDisponibilidadStaff(idUsuario?: number) {
  const [rows, setRows] = useState<DisponibilidadStaffRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<DisponibilidadStaffRow[]> => {
    if (!idUsuario) return [];
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<DisponibilidadStaffRow[]>(
        `/disponibilidad-staff?idUsuario=${idUsuario}`,
      );
      setRows(data ?? []);
      return data ?? [];
    } catch (err: any) {
      setError(err.message || 'Error al cargar disponibilidad');
      return [];
    } finally {
      setLoading(false);
    }
  }, [idUsuario]);

  const create = useCallback(
    async (dto: CreateDisponibilidadStaffDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post<DisponibilidadStaffRow>('/disponibilidad-staff', dto);
        await refresh();
        return data;
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Error al crear disponibilidad');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refresh],
  );

  const update = useCallback(
    async (id: number, dto: UpdateDisponibilidadStaffDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.put<DisponibilidadStaffRow>(`/disponibilidad-staff/${id}`, dto);
        await refresh();
        return data;
      } catch (err: any) {
        setError(
          err.response?.data?.message || err.message || 'Error al actualizar disponibilidad',
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refresh],
  );

const remove = useCallback(
  async (id: number) => {
    // NO uses setLoading aquí
    setError(null);
    try {
      await api.delete(`/disponibilidad-staff/${id}`);
      // 🔹 Actualiza el estado local inmediatamente
      setRows(prev => prev.filter(row => row.id !== id));
      return Promise.resolve();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al eliminar disponibilidad';
      setError(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }
  },
  [],
);
  // Filtrar por día de la semana si se necesita
  const getByDiaSemana = useCallback(
    (diaSemana: number) => {
      return rows.filter((row) => row.diaSemana === diaSemana);
    },
    [rows],
  );

  return {
    rows,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
    getByDiaSemana,
  };
}
